// Git Unstage Tool - Unstages files from the Git repository
// Extends WorkspaceTool - has access to appType, fs, and git
// Removes files from the staging area

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitUnstageTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitUnstageTool';
  }
  /**
   * Unstage files from the Git repository
   * @param {string|string[]} [filepaths=[]] - File path(s) to unstage (use '*' or [] for all)
   * @returns {Promise<Object>} Result with unstaging status
   */
  async gitUnstage(filepaths = []) {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitUnstageForBrowser(filepaths);
      }

      if (this.appType === "local") {
        return await this.gitUnstageForLocal(filepaths);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitUnstage`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitunstage|error",
          color1: "red",
        },
        "Failed to unstage files",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git unstage for browser apps using isomorphic-git
   */
  async gitUnstageForBrowser(filepaths) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    // Check if .git exists
    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      return this.returnError({}, "Git repository not initialized");
    }

    // Normalize filepaths to array
    let filesToUnstage = [];
    if (
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0)
    ) {
      // Unstage all files - get all staged files from status
      const statusResult = await this.git.statusMatrix({
        fs: this.fs,
        dir,
        filter: (f) => {
          return (
            !f.startsWith(".git/") &&
            f !== ".git" &&
            !f.startsWith(".revolt/") &&
            f !== ".revolt"
          );
        },
      });

      // Get files that are staged
      // A file is staged if the index differs from HEAD
      // For deletions: stageStatus === 0 (removed from index) but headStatus !== 0 (exists in HEAD)
      // For additions/modifications: stageStatus !== 0 and stageStatus !== headStatus
      for (const [filepath, headStatus, , stageStatus] of statusResult) {
        if (
          filepath.startsWith(".git/") ||
          filepath === ".git" ||
          filepath.startsWith(".revolt/") ||
          filepath === ".revolt"
        ) {
          continue;
        }
        // Check if file is staged (index differs from HEAD)
        const isStaged = headStatus !== stageStatus;
        if (isStaged) {
          filesToUnstage.push(filepath);
        }
      }
    } else if (typeof filepaths === "string") {
      filesToUnstage = [filepaths];
    } else if (Array.isArray(filepaths)) {
      filesToUnstage = filepaths;
    } else {
      return this.returnError({}, "Invalid filepaths parameter");
    }

    if (filesToUnstage.length === 0) {
      return this.returnSuccess(
        {
          unstagedCount: 0,
          unstagedFiles: [],
        },
        "No files to unstage"
      );
    }

    // Unstage each file using git reset
    const unstagedFiles = [];
    const errors = [];

    for (const filepath of filesToUnstage) {
      try {
        await this.git.resetIndex({
          fs: this.fs,
          dir,
          filepath,
        });
        unstagedFiles.push(filepath);
      } catch (error) {
        errors.push({ path: filepath, error: error.message });
        logger.warn(
          {
            tags: "tools|gitunstage|reset",
            color1: "yellow",
          },
          `Failed to unstage file: ${filepath}`,
          error
        );
      }
    }

    logger.log(
      {
        tags: "tools|gitunstage|info",
        color1: "lime",
      },
      `Unstaged ${unstagedFiles.length} files`,
      {
        unstagedCount: unstagedFiles.length,
        errors: errors.length,
      }
    );

    return this.returnSuccess(
      {
        unstagedCount: unstagedFiles.length,
        unstagedFiles,
        errors: errors.length > 0 ? errors : undefined,
      },
      `Unstaged ${unstagedFiles.length} file(s) successfully`
    );
  }

  /**
   * Unstage files via Revolt Desktop IPC (`git reset HEAD`).
   */
  async gitUnstageForLocal(filepaths) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitUnstage) {
      return this.returnError(
        {},
        "Local git unstage requires Revolt Desktop (workspaceLocalAppAPI.gitUnstage)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const unstageAll =
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0);

    const res = await api.gitUnstage({
      rootPath: root,
      paths: unstageAll ? "*" : filepaths,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to unstage files");
    }

    const unstagedFiles = res.unstagedFiles || [];
    const unstagedCount =
      typeof res.unstagedCount === "number"
        ? res.unstagedCount
        : unstagedFiles.length;

    logger.log(
      { tags: "tools|gitunstage|local|info", color1: "lime" },
      `Unstaged ${unstagedCount} files (local)`
    );

    return this.returnSuccess(
      {
        unstagedCount,
        unstagedFiles,
        errors: res.errors,
      },
      unstagedCount > 0
        ? `Unstaged ${unstagedCount} file(s) successfully`
        : "No files to unstage"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}
